import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPublicProfile,
  likeImage,
  commentImage,
  getImageComments,
  editImageComment,
  deleteImageComment,
  enviarSolicitudAmistad,
  cancelarAmistad,
} from "../services/api";
import { useWebSocketActions } from "../context/WebSocketContext"; // 👈 Importar
import CommentForm from "../components/posts/CommentForm";
import CommentList from "../components/posts/CommentList";
import countriesData from "../data/countries";
import { toast } from "react-hot-toast";
import { Heart, MessageCircle } from "lucide-react";

const calcularEdad = (fechaNacimiento) => {
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }
  return edad;
};

const ProfilePublicPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [friendStatus, setFriendStatus] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  // HAPPYCAP_PROFILE_IMAGE_STATS_V1
  const [imageStats, setImageStats] = useState({});

  const { addListener } = useWebSocketActions(); // 👈

  const fetchProfile = async () => {
    try {
      const res = await getPublicProfile(id);
      setProfile(res.data);
      setFriendStatus(res.data.friendship_status);
    } catch (err) {
      console.error("Error cargando el perfil público", err);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [id]);

  // ✅ Actualizar si se recibe una aceptación de amistad
  useEffect(() => {
    const remove = addListener((data) => {
      if (
        data.type === "notification" &&
        data.data.type === "friend_request_accepted" &&
        String(data.data.sender_id) === String(id)
      ) {
        console.log("🟢 Actualizando perfil tras aceptación");
        fetchProfile();
      }
    });

    return () => remove();
  }, [addListener, id]);

  useEffect(() => {
    const loadImageDetails = async () => {
      if (modalImage) {
        try {
          const res = await getImageComments(modalImage.id);
          const formattedComments = (res.data.comments || []).map((c) => ({
            ...c,
            author_name: `${c.profile_nombre ?? ""} ${c.profile_apellidos ?? ""}`.trim(),
          }));
          setLikes(res.data.likes || 0);
          setLiked(res.data.liked_by_user || false);
          setComments(formattedComments);
          setImageStats((prev) => ({
            ...prev,
            [modalImage.id]: {
              likes: res.data.likes ?? 0,
              comments: formattedComments.length,
            },
          }));
          setShowComments(false);
        } catch (err) {
          console.error("Error al cargar datos de la imagen", err);
        }
      }
    };
    loadImageDetails();
  }, [modalImage]);

  // Cargar contadores reales de las fotografías visibles para amigos.
  useEffect(() => {
    const imagenes = profile?.imagenes || [];

    if (friendStatus !== "accepted" || !imagenes.length) {
      setImageStats({});
      return;
    }

    let cancelado = false;

    // Evitar mostrar contadores de un perfil anterior.
    setImageStats({});

    const cargarContadores = async () => {
      const resultados = await Promise.allSettled(
        imagenes.map((img) => getImageComments(img.id))
      );

      if (cancelado) return;

      const estadisticas = {};

      resultados.forEach((resultado, index) => {
        if (resultado.status === "fulfilled") {
          const datos = resultado.value.data;
          estadisticas[imagenes[index].id] = {
            likes: datos.likes ?? 0,
            comments: (datos.comments || []).length,
          };
        }
      });

      setImageStats(estadisticas);
    };

    cargarContadores();

    return () => {
      cancelado = true;
    };
  }, [profile, friendStatus]);

  const handleLike = async () => {
    try {
      const res = await likeImage(modalImage.id);
      setLikes(res.data.total_likes);
      setLiked(res.data.liked);
      setImageStats((prev) => ({
        ...prev,
        [modalImage.id]: {
          likes: res.data.total_likes,
          comments: prev[modalImage.id]?.comments ?? comments.length,
        },
      }));
    } catch (err) {
      console.error("Error al dar like", err);
    }
  };

  const handleNewComment = async (text) => {
    try {
      const res = await commentImage(modalImage.id, text);
      const newComment = {
        ...res.data,
        author_name: `${res.data.profile_nombre ?? ""} ${res.data.profile_apellidos ?? ""}`.trim(),
      };
      setComments((prev) => [...prev, newComment]);
      setImageStats((prev) => ({
        ...prev,
        [modalImage.id]: {
          likes: prev[modalImage.id]?.likes ?? likes,
          comments: (prev[modalImage.id]?.comments ?? comments.length) + 1,
        },
      }));
    } catch (err) {
      console.error("Error al comentar", err);
    }
  };

  const handleImageCommentUpdated = (updatedComment) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === updatedComment.id
          ? {
              ...comment,
              ...updatedComment,
              author_name:
                updatedComment.author_name || comment.author_name,
            }
          : comment
      )
    );
  };

  const handleImageCommentDeleted = (commentId) => {
    setComments((prev) =>
      prev.filter((comment) => comment.id !== commentId)
    );
    setImageStats((prev) => ({
      ...prev,
      [modalImage.id]: {
        likes: prev[modalImage.id]?.likes ?? likes,
        comments: Math.max(
          0,
          (prev[modalImage.id]?.comments ?? comments.length) - 1
        ),
      },
    }));
  };

  const handleFollow = async () => {
    try {
      if (friendStatus === "accepted") {
        await cancelarAmistad(profile.id);
        toast.success(t("profile.unfollow_success", "Has dejado de seguir a este usuario"));
      } else {
        await enviarSolicitudAmistad(profile.id);
        toast.success(t("profile.request_sent", "Solicitud de amistad enviada"));
        setFriendStatus("pending");
      }

      const res = await getPublicProfile(profile.id);
      setFriendStatus(res.data.friendship_status);
      setProfile(res.data);
    } catch (err) {
      toast.error(t("profile.follow_error", "Error al modificar amistad"));
    }
  };

  const getCountryLabel = (code) => {
    const found = countriesData.find((c) => c.countryCode === code);
    return found?.countryLabel || code;
  };

  const getCityLabel = (cityCode, countryCode) => {
    const country = countriesData.find((c) => c.countryCode === countryCode);
    return country?.cityCodes.find((city) => city.toLowerCase() === cityCode?.toLowerCase()) || cityCode;
  };

  const renderFollowButton = () => {
    if (friendStatus === "self") return null;
    switch (friendStatus) {
      case "pending":
        return (
          <button className="mt-5 rounded-xl border border-slate-200 bg-slate-100 px-5 py-2.5 text-sm font-medium text-slate-500 cursor-not-allowed" disabled>
            {t("profile.request_pending")}
          </button>
        );
      case "received":
        return (
          <button className="mt-5 rounded-xl border border-[#B8D8EA] bg-[#EFF7FC] px-5 py-2.5 text-sm font-medium text-[#245580] cursor-not-allowed" disabled>
            {t("profile.request_received")}
          </button>
        );
      case "accepted":
        return (
          <button
            onClick={handleFollow}
            className="mt-5 rounded-xl border border-red-200 bg-white px-5 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
          >
            {t("profile.unfollow")}
          </button>
        );
      default:
        return (
          <button
            onClick={handleFollow}
            className="mt-5 rounded-xl bg-[#16A34A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#15803D] hover:shadow-md"
          >
            {t("profile.send_request")}
          </button>
        );
    }
  };

  if (!profile) {
    return (
      <div className="mx-auto mt-12 max-w-4xl px-4 text-center text-slate-500">
        {t("loading", "Cargando perfil...")}
      </div>
    );
  }

  const datos = [
    { icono: "🗣️", titulo: t("profile.habla"), valor: profile.habla },
    { icono: "✍️", titulo: t("profile.escribe"), valor: profile.escribe },
    { icono: "🚶", titulo: t("profile.movilidad"), valor: profile.movilidad },
    { icono: "🤲", titulo: t("profile.motricidad"), valor: profile.motricidad },
    { icono: "👁️", titulo: t("profile.ver"), valor: profile.ver },
    { icono: "👂", titulo: t("profile.audition"), valor: profile.audition },
    { icono: "🧠", titulo: t("profile.memoire"), valor: profile.memoire },
    { icono: "🧭", titulo: t("profile.orientation"), valor: profile.orientation },
    { icono: "🧩", titulo: t("profile.concentration"), valor: profile.concentration },
  ];

  const getCountryFlag = (countryCode) => {
    if (!countryCode || typeof countryCode !== "string") return "";

    const code = countryCode.trim().toUpperCase();

    if (!/^[A-Z]{2}$/.test(code)) return "";

    return String.fromCodePoint(
      ...Array.from(code).map(
        (letter) => 127397 + letter.charCodeAt(0)
      )
    );
  };

  const ciudad = profile.ciudad
    ? getCityLabel(profile.ciudad, profile.country)
    : "";

  const pais = profile.country
    ? getCountryLabel(profile.country)
    : "";

  const ubicacion = [ciudad, pais].filter(Boolean).join(", ");
  const bandera = getCountryFlag(profile.country);

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">

      {/* CABECERA DEL PERFIL */}
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">

          <img
            src={profile.photo || "/default-avatar.png"}
            alt={t("profile.photo_alt", "Foto de perfil")}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/default-avatar.png";
            }}
            className="h-32 w-32 shrink-0 rounded-full border-4 border-white object-cover shadow-lg ring-1 ring-slate-200 sm:h-36 sm:w-36"
          />

          <div className="min-w-0 flex-1 text-center sm:text-left">
            <h1 className="break-words text-2xl font-bold tracking-tight text-[#245580] sm:text-3xl">
              {profile.nombre} {profile.apellidos}
            </h1>

            {ubicacion && (
              <p className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-500 sm:justify-start">
                {bandera && (
                  <span
                    className="text-xl leading-none"
                    aria-hidden="true"
                  >
                    {bandera}
                  </span>
                )}
                <span>{ubicacion}</span>
              </p>
            )}

            {profile.date_of_birth && (
              <p className="mt-1 text-sm text-slate-500">
                {calcularEdad(profile.date_of_birth)} {t("profile.years_old")}
              </p>
            )}

            <div className="mt-4 flex justify-center sm:justify-start">
              {renderFollowButton()}
            </div>
          </div>
        </div>
      </section>

      {/* INFORMACIÓN PERSONAL - HAPPYCAP_PROFILE_FEATURE_CARDS_V1 */}
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {datos.map((dato, index) => (
            <div
              key={index}
              className="group flex min-w-0 items-start gap-3 rounded-[20px] border border-[#E3EDF4] bg-[#F7FAFD] p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#C9DFED] hover:bg-[#F1F8FC] hover:shadow-sm sm:p-5"
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E1EDF5] bg-white text-xl shadow-sm"
                aria-hidden="true"
              >
                {dato.icono}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#648098]">
                  {dato.titulo}
                </p>

                <p className="mt-1.5 break-words text-[15px] font-semibold leading-6 text-[#245580]">
                  {dato.valor || "—"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BIOGRAFÍA */}
      {profile.bio && (
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-lg font-bold text-[#245580]">
            {t("profile.bio", "Sobre mí")}
          </h2>

          <p className="mt-4 whitespace-pre-line text-sm leading-7 text-slate-600">
            {profile.bio}
          </p>
        </section>
      )}

      {/* GALERÍA - HAPPYCAP_PROFILE_GALLERY_HARMONY_V1 */}
      {friendStatus === "accepted" && profile.imagenes?.length > 0 && (
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <h2 className="mb-6 text-lg font-bold text-[#245580]">
            {t("profile.gallery", "Galería")}
          </h2>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {profile.imagenes.map((img) => (
              <article
                key={img.id}
                className="group overflow-hidden rounded-[24px] border border-[#E5EDF3] bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-md"
              >
                <button
                  type="button"
                  onClick={() => setModalImage(img)}
                  aria-label={img.title || t("profile.gallery", "Galería")}
                  className="block w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#247BA8]"
                >
                  <img
                    src={img.image_url}
                    alt={img.title || ""}
                    loading="lazy"
                    className="h-56 w-full object-cover transition duration-300 group-hover:scale-105 sm:h-60"
                  />
                </button>

                <div className="px-5 pb-4 pt-4">
                  {img.title && (
                    <h3 className="mb-3 break-words text-sm font-semibold text-[#245580]">
                      {img.title}
                    </h3>
                  )}

                  <div className="flex items-center gap-5 border-t border-slate-100 pt-3">
                    <button
                      type="button"
                      onClick={() => setModalImage(img)}
                      aria-label={t("image_gallery.likes", "Me gusta")}
                      className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-rose-500"
                    >
                      <Heart size={19} strokeWidth={1.8} aria-hidden="true" />
                      <span>{imageStats[img.id]?.likes ?? "…"}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setModalImage(img)}
                      aria-label={t("image_gallery.comments", "Comentarios")}
                      className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-[#247BA8]"
                    >
                      <MessageCircle size={19} strokeWidth={1.8} aria-hidden="true" />
                      <span>{imageStats[img.id]?.comments ?? "…"}</span>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {friendStatus !== "accepted" && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-center text-sm text-slate-500">
          {t("profile.gallery_locked")}
        </div>
      )}


      {/* HAPPYCAP_PROFILE_COMMENTS_MODERN_V1 */}
      <style>{`
        .profile-gallery-comments-modern form {
          gap: 10px !important;
          align-items: stretch !important;
        }

        .profile-gallery-comments-modern form input[type="text"] {
          border: 1px solid #d8e7f0 !important;
          border-radius: 14px !important;
          background: #ffffff !important;
          padding: 12px 14px !important;
          font-size: 14px !important;
          outline: none !important;
        }

        .profile-gallery-comments-modern form input[type="text"]:focus {
          border-color: #247ba8 !important;
          box-shadow: 0 0 0 2px rgba(36, 123, 168, 0.12);
        }

        .profile-gallery-comments-modern form button[type="submit"] {
          align-self: flex-end !important;
          border-radius: 999px !important;
          background: #247ba8 !important;
          padding: 9px 20px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
        }

        .profile-gallery-comments-modern form button[type="submit"]:hover:not(:disabled) {
          background: #19658e !important;
        }

        .profile-gallery-comments-modern form button[type="submit"]:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      {/* MODAL DE IMAGEN */}
      {modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/75 px-3 py-8 sm:py-12"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative w-full max-w-2xl rounded-3xl bg-white p-5 shadow-2xl sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setModalImage(null)}
              aria-label={t("common.close", "Cerrar")}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow transition hover:bg-slate-100"
            >
              ✕
            </button>

            <img
              src={modalImage.image_url}
              alt={modalImage.title || ""}
              className="max-h-[65vh] w-full rounded-2xl bg-slate-50 object-contain"
            />

            {modalImage.title && (
              <h3 className="mt-5 text-lg font-bold text-[#245580]">
                {modalImage.title}
              </h3>
            )}

            {modalImage.description && (
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {modalImage.description}
              </p>
            )}

            <div className="mt-5 flex justify-center gap-4 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={handleLike}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#D8E7F0] bg-white px-5 py-2 text-sm font-medium text-[#245580] transition hover:bg-[#F2F8FC]"
              >
                {liked ? "💖" : "❤️"} {likes}
              </button>

              <button
                type="button"
                onClick={() => setShowComments((prev) => !prev)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#D8E7F0] bg-white px-5 py-2 text-sm font-medium text-[#245580] transition hover:bg-[#F2F8FC]"
              >
                💬 {comments.length}
              </button>
            </div>

            {showComments && (
              <div className="profile-gallery-comments-modern mt-5 rounded-2xl bg-[#F5F9FC] p-4 sm:p-5">
                <CommentForm onSubmit={handleNewComment} />
                <CommentList
                  comments={comments}
                  onCommentUpdated={handleImageCommentUpdated}
                  onCommentDeleted={handleImageCommentDeleted}
                  editCommentAction={editImageComment}
                  deleteCommentAction={deleteImageComment}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );

};

export default ProfilePublicPage;
